import React, { useState, useEffect, useCallback } from 'react';
import { useData } from '../../contexts/DataContext';
import { useToast } from '../../contexts/ToastContext';
import SidebarItemMenu from './SidebarItemMenu';
import ProcessingSection from './ProcessingSection';
import { useGenerationJobs } from '../../hooks/useGenerationJobs';
import './SidebarItemMenu.css';
import './QuizHistoryPanel.css';

const QUIZ_PIN_KEY = 'quizHistory.pinnedIds';

const QuizHistoryPanel = ({ activeQuizId, onSelectQuiz, onNewQuiz, hideHeader = false, hideSearch = false, showNewButton = true, showSectionHeader = false, onToggleCollapse, sidebarCollapsed, externalSearchQuery }) => {
  const { getQuizSets, getLatestAttempt, clearCache, updateQuizSet, deleteQuizSet } = useData();
  const { addToast } = useToast();
  const { processingJobs, failedJobs, dismissJob: dismissGenJob } = useGenerationJobs('quiz');
  const [quizSets, setQuizSets] = useState([]);
  const [quizAttempts, setQuizAttempts] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [pinnedIds, setPinnedIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(QUIZ_PIN_KEY)) || []; } catch { return []; }
  });

  const togglePin = useCallback((id) => {
    setPinnedIds(prev => {
      const next = prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id];
      try { localStorage.setItem(QUIZ_PIN_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const handleRename = useCallback((id, currentTitle) => {
    setRenaming(id);
    setRenameValue(currentTitle || '');
  }, []);

  const handleRenameSubmit = useCallback(async (id) => {
    const trimmed = renameValue.trim();
    if (!trimmed) { setRenaming(null); return; }
    try {
      await updateQuizSet(id, { title: trimmed });
      setQuizSets(prev => prev.map(q => q.id === id ? { ...q, title: trimmed } : q));
      addToast({ intent: 'success', message: 'Quiz renamed' });
    } catch (err) {
      console.error('Failed to rename quiz:', err);
      addToast({ intent: 'danger', message: 'Failed to rename quiz' });
    }
    setRenaming(null);
    setRenameValue('');
  }, [renameValue, updateQuizSet, addToast]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Are you sure you want to delete this quiz?')) return;
    try {
      await deleteQuizSet(id);
      setQuizSets(prev => prev.filter(q => q.id !== id));
      clearCache('quiz-sets');
      addToast({ intent: 'success', message: 'Quiz deleted' });
    } catch (err) {
      console.error('Failed to delete quiz:', err);
      addToast({ intent: 'danger', message: 'Failed to delete quiz' });
    }
  }, [deleteQuizSet, clearCache, addToast]);

  // Listen for quiz creation events to refresh the list
  useEffect(() => {
    const handleQuizCreated = () => {
      clearCache('quiz-sets');
      setRefreshCounter(c => c + 1);
    };
    window.addEventListener('quizzes:created', handleQuizCreated);
    return () => window.removeEventListener('quizzes:created', handleQuizCreated);
  }, [clearCache]);

  useEffect(() => {
    const fetchQuizzes = async () => {
      setIsLoading(true);
      try {
        const quizSetsResponse = await getQuizSets();
        setQuizSets(quizSetsResponse || []);
        
        // Fetch latest attempt for each quiz set
        const attemptPromises = (quizSetsResponse || []).map(async (quiz) => {
          try {
            const attempt = await getLatestAttempt(quiz.id);
            return { quizSetId: quiz.id, attempt };
          } catch (err) {
            // 404 is expected if no attempts exist
            if (err?.status !== 404) {
              console.warn(`Failed to fetch attempt for quiz ${quiz.id}:`, err);
            }
            return { quizSetId: quiz.id, attempt: null };
          }
        });
        
        const attempts = await Promise.all(attemptPromises);
        const attemptsMap = {};
        attempts.forEach(({ quizSetId, attempt }) => {
          attemptsMap[quizSetId] = attempt;
        });
        setQuizAttempts(attemptsMap);
      } catch (error) {
        console.error('Failed to load quiz sets:', error);
        addToast({ intent: 'danger', message: 'Failed to load quizzes' });
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuizzes();
  }, [getQuizSets, getLatestAttempt, addToast, refreshCounter]);



  const effectiveSearchQuery = externalSearchQuery != null ? externalSearchQuery : searchQuery;
  const filteredQuizzes = (quizSets?.filter(quiz => 
    quiz.title?.toLowerCase().includes(effectiveSearchQuery.toLowerCase())
  ) || []).sort((a, b) => {
    const ap = pinnedIds.includes(a.id) ? 0 : 1;
    const bp = pinnedIds.includes(b.id) ? 0 : 1;
    return ap - bp;
  });

  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    
    // Prevent negative times
    if (diffMs < 0) return 'just now';
    
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getScoreColor = (scorePercent) => { // eslint-disable-line no-unused-vars
    if (!scorePercent) return 'gray';
    if (scorePercent >= 90) return 'green';
    if (scorePercent >= 70) return 'blue';
    if (scorePercent >= 50) return 'orange';
    return 'red';
  };

  if (isLoading) {
    return (
      <div className="quiz-history-panel">
        <div className="quiz-history-panel__header">
          <h3 className="quiz-history-panel__title">My Quizzes</h3>
        </div>
        <div className="quiz-history-panel__loading">
          <div className="spinner"></div>
          <p>Loading quizzes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="quiz-history-panel">
      {!hideHeader && (
        <div className="quiz-history-panel__header">
          <div className="quiz-history-panel__header-row">
            {onToggleCollapse && (
              <button
                type="button"
                className="quiz-history-panel__collapse-btn"
                onClick={onToggleCollapse}
                title="Collapse sidebar"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M9 4l-3 4 3 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5 4l-3 4 3 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
            
            {!hideSearch && (
              <div className="quiz-history-panel__search">
                <input
                  type="text"
                  placeholder="Search quizzes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="quiz-history-panel__search-input"
                />
              </div>
            )}
            
            {onNewQuiz && (
              <button 
                className="quiz-history-panel__circular-new-btn"
                onClick={onNewQuiz}
                title="Create New Quiz"
              >
                +
              </button>
            )}
          </div>
        </div>
      )}

      {/* Section Header - sticky within scroll area */}
      {showSectionHeader && (
        <div className="quiz-history-section-header">
          <span className="quiz-history-section-title">Quiz History</span>
          <span className="quiz-history-count-pill">{filteredQuizzes.length}</span>
        </div>
      )}

      <div className="quiz-history-panel__list">
        <ProcessingSection
          processingJobs={processingJobs}
          failedJobs={failedJobs}
          onDismiss={dismissGenJob}
        />
        {filteredQuizzes.length === 0 && processingJobs.length === 0 && failedJobs.length === 0 ? (
          <div className="quiz-history-panel__empty">
            <div className="quiz-history-panel__tutorial">
              <div className="quiz-history-panel__tutorial-badge">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
              </div>
              <h4 className="quiz-history-panel__tutorial-title">
                Generate Your First Quiz
              </h4>
              <p className="quiz-history-panel__tutorial-text">
                Turn your course materials into practice questions and test yourself in minutes.
              </p>
              <p className="quiz-history-panel__tutorial-hint">Best with lecture slides, study guides, and readings</p>
            </div>
          </div>
        ) : (
          filteredQuizzes.map((quiz) => {
            const latestAttempt = quizAttempts[quiz.id];
            const isPinned = pinnedIds.includes(quiz.id);
            
            return (
            <div
              key={quiz.id}
              className={`qh-card ${activeQuizId === quiz.id ? 'is-active' : ''} ${isPinned ? 'is-pinned' : ''}`}
              onClick={() => { if (renaming !== quiz.id) onSelectQuiz && onSelectQuiz(quiz); }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectQuiz && onSelectQuiz(quiz); } }}
            >
              {/* Title */}
              <div className="qh-top">
                {isPinned && (
                  <span className="qh-pin-icon">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2"><path d="M12 17v5"/><path d="M9 2h6l-1 7h4l-5 8h-2l-5-8h4Z"/></svg>
                  </span>
                )}
                <div className="qh-title" title={quiz.title || `Quiz ${quiz.id}`}>
                  {renaming === quiz.id ? (
                    <input
                      className="sim-rename-input"
                      type="text"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onBlur={() => handleRenameSubmit(quiz.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleRenameSubmit(quiz.id);
                        else if (e.key === 'Escape') { setRenaming(null); setRenameValue(''); }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  ) : (
                    quiz.title || `Quiz ${quiz.id}`
                  )}
                </div>
                <SidebarItemMenu
                  onRename={() => handleRename(quiz.id, quiz.title)}
                  onPin={() => togglePin(quiz.id)}
                  isPinned={isPinned}
                  onDelete={() => handleDelete(quiz.id)}
                />
              </div>
              
              {/* Metadata chips */}
              <div className="qh-meta">
                <span className="qh-chip">📝 {quiz.numQuestions} questions</span>
                <span className="qh-chip">⏱️ {formatDate(latestAttempt?.completedAt || quiz.createdAt)}</span>
                {quiz.documentIds && quiz.documentIds.length > 0 && (
                  <span className="qh-chip">
                    📄 {quiz.documentIds.length} doc{quiz.documentIds.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>

              {/* Difficulty badge */}
              {quiz.difficultyLevel && (
                <div className="qh-bottom">
                  <span className={`qh-badge ${quiz.difficultyLevel.toLowerCase()}`}>
                    {quiz.difficultyLevel}
                  </span>
                </div>
              )}
            </div>
            );
          })
        )}
      </div>

    </div>
  );
};

export default QuizHistoryPanel;
